import {useDispatch} from 'react-redux';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {omneticApi} from '@dms/api/base';
import {notificationTypes} from '@dms/shared';

import {useDebouncedCallback} from 'shared/web';

const REFETCH_DEBOUNCE_MS = 500;

interface UseOnlineOfferEngagementDoorbellArgs {
  businessCaseId: string;
}

/**
 * Refetches the business case and its activities when the customer engages with an online offer.
 *
 * The online-offer app writes that engagement with no client signal in DMS, so the backend rings
 * `business_case-online_offer_engagement_changed`. The payload only says WHICH business case
 * moved and is not rendered.
 */
export const useOnlineOfferEngagementDoorbell = (args: UseOnlineOfferEngagementDoorbellArgs) => {
  const dispatch = useDispatch();

  const refetchBusinessCase = useDebouncedCallback(() => {
    dispatch(
      omneticApi.util.invalidateTags([
        {type: 'BusinessCaseDetail', id: args.businessCaseId},
        {type: 'BusinessCaseActivity', id: args.businessCaseId},
      ])
    );
  }, REFETCH_DEBOUNCE_MS);

  useSubscribeToNotificationsQuery((notification) => {
    // Runs inside the subscription effect; a payload off the contract must not throw there.
    if (
      notification.type !== notificationTypes.BUSINESS_CASE_ONLINE_OFFER_ENGAGEMENT_CHANGED ||
      notification.payload?.businessCaseId !== args.businessCaseId
    ) {
      return;
    }

    refetchBusinessCase();
  });
};
